import Translator from "../utils/translator";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { SubscriptionsService } from "../services/base";
import Api from "./api";
import { setLocale } from "../utils/dayjs";
import { DriveStore } from "../store/reducers";
import { UserService } from "../utils/users";
import { IFeatureFlagsService } from "./featureFlags";

const SUPPORTED_LANGUAGES = [
    "en",
    "fr",
    "es",
    "ja",
    "de",
    "pl",
    "pt-BR",
    "nl",
    "it",
    "zh-Hans",
    "th",
    "id",
    "cs",
    "sv",
    "tr",
    "ms",
    "fr-FR",
    "ko-KR",
    "zh-TW",
    "da-DK",
    "fi-FI",
    "hu-HU",
    "nb-NO",
    "sk-SK",
    "pt-PT",
    "ro-RO",
    "es-ES",
    "bg-BG",
    "el-GR",
    "et-EE",
    "ka-GE",
    "lt-LT",
    "lv-LV",
    "ar-001"
];

export default class TranslationService extends SubscriptionsService {
    private readonly api: Api;
    private readonly user: string | null;
    private readonly store: DriveStore;
    private translator: Translator | null = null;

    constructor(
        store: DriveStore,
        api: Api,
        user: string | null,
        private readonly userService: UserService,
        private readonly featureFlagsService: IFeatureFlagsService
    ) {
        super();
        this.api = api;
        this.user = user;
        this.store = store;
        this.updateTranslator((user && this.userService.getUserByName(user)) || null);
        this.getTranslator = this.getTranslator.bind(this);
    }

    getTranslator(): Translator {
        return this.translator || new Translator(this.getPreferredLanguage(), this.api.driveApi, this.store, this.featureFlagsService);
    }

    setLanguage(language: string) {
        const translationLanguage = typeof language === "string" && ["es-419", "es-es"].indexOf(language.toLowerCase()) > -1 ? this.getSupportedLanguage(language) : language;
        let hadLanguageUpdate = false;
        const dayjsLanguages: Record<string, string> = {
            "pt-BR": "pt-br",
            "zh-Hans": "zh-cn",
            "fr-FR": "fr",
            fr: "fr-ca",
            "ko-KR": "ko",
            "zh-TW": "zh-tw",
            "da-DK": "da",
            "fi-FI": "fi",
            "hu-HU": "hu",
            "nb-NO": "nb",
            "sk-SK": "sk",
            "pt-PT": "pt",
            "ro-RO": "ro",
            "es-ES": "es",
            "es-419": "es",
            "bg-BG": "bg",
            "el-GR": "el",
            "et-EE": "et",
            "ka-GE": "ka",
            "lt-LT": "lt",
            "lv-LV": "lv",
            // Day.js does not provide an ar-001 locale bundle.
            "ar-001": "ar"
        };
        const dayjsLanguage = dayjsLanguages[translationLanguage] ?? translationLanguage;

        if (!this.translator || (translationLanguage && this.translator.language !== translationLanguage)) {
            this.translator = new Translator(translationLanguage, this.api.driveApi, this.store, this.featureFlagsService);
            hadLanguageUpdate = true;
        }

        this.translator.validateCache(translationLanguage);

        let thisUser = this.user;

        // Handle first time login (no current user)
        if (!this.userService.getCurrentUser() || (thisUser && this.userService.isDriverUsingUI(thisUser))) {
            setLocale(dayjsLanguage);
        }
        return hadLanguageUpdate;
    }

    getLanguage() {
        return this.translator ? this.translator.language : this.getPreferredLanguage();
    }

    startSubscriptions() {
        this.stopSubscriptions();
        // eslint-disable-next-line @typescript-eslint/unbound-method
        dispatcher.on(DispatcherEvents.UserUpdate, this.onUserUpdate, this);
    }

    stopSubscriptions() {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        dispatcher.off(DispatcherEvents.UserUpdate, this.onUserUpdate, this);
    }

    destroy() {
        this.translator = null;
        super.destroy();
    }

    private updateTranslator(user: DeepReadonly<IUser> | null) {
        let userLanguage = (user && user.language) || this.getPreferredLanguage();
        return this.setLanguage(this.getSupportedLanguage(userLanguage));
    }

    private onUserUpdate(newUser: IUser) {
        let hadLanguageUpdate = this.updateTranslator(newUser);
        if (hadLanguageUpdate) {
            dispatcher.trigger(DispatcherEvents.UserUpdateLanguage, newUser);
        }
    }

    private getSupportedLanguage(language: string) {
        const normalizedLanguage = language.toLowerCase();
        if (normalizedLanguage === "es-419") {
            return "es-419";
        }
        if (normalizedLanguage === "es-es") {
            return "es-ES";
        }
        if (normalizedLanguage === "ar" || normalizedLanguage.startsWith("ar-")) {
            return "ar-001";
        }
        return SUPPORTED_LANGUAGES.indexOf(language) > -1 ? language : "en";
    }

    private getPreferredLanguage() {
        let language = navigator.language || "en-US";

        if (["es-419", "es-es"].indexOf(language.toLowerCase()) > -1) {
            return this.getSupportedLanguage(language);
        }
        return SUPPORTED_LANGUAGES.indexOf(language) > -1 ? language : this.getSupportedLanguage(language.substring(0, 2).toLowerCase());
    }
}
